/**
 * Status resource hook. Single endpoint: `GET /api/status`.
 *
 * Wraps the Orval-generated `useStatusGet` to encode the maintenance-mode
 * polling cadence and shape the return value into something the SPA's
 * `RootGate` can consume without manual envelope-walking.
 *
 * Behaviour:
 *  - `staleTime: 15_000` — `RootGate` is a parent layout, so a navigation
 *    that re-renders the gate inside the 15s window doesn't refetch.
 *  - `refetchInterval: 30_000` — when an operator flips `maintenance-mode`
 *    off the page auto-clears within 30s; no manual reload needed.
 *  - `refetchIntervalInBackground: false` — don't burn cycles in inactive
 *    tabs. The user sees a fresh value on focus regardless because the
 *    query's `staleTime` is short.
 *
 * `maintenance` defaults to `false` while the query is loading OR errored
 * — fail OPEN, not closed. The endpoint is anonymous and on the boot path:
 * defaulting to `true` would paint the maintenance page on every initial
 * mount, and a transient `/api/status` 5xx would lock every user out of
 * the app even though nothing is actually in maintenance. Fail-open is the
 * correct posture for a kill-switch read.
 *
 * Observability: fail-open is silent by design (no toast, no redirect),
 * but the first transition into an errored state surfaces a single
 * `console.warn` so an operator inspecting devtools sees the failure
 * mode rather than mistaking it for "kill-switch off." A `useRef` latch
 * keeps the warning to one-per-error-streak; recovery resets the latch.
 */
import { getStatusGetQueryKey, useStatusGet } from '@/api/generated';
import { useEffect, useRef } from 'react';

export interface UseMaintenanceStatusResult {
  maintenance: boolean;
  isError: boolean;
  // First-load gate. Deliberately the ONLY loading signal exposed: gating UI
  // on the raw React Query `isLoading` is the bug class (landing-page flicker)
  // this hook exists to prevent, so it is not surfaced — consumers block on
  // `isInitialLoad` (true only before the first settle) instead.
  isInitialLoad: boolean;
}

export function useMaintenanceStatus(): UseMaintenanceStatusResult {
  const query = useStatusGet({
    query: {
      queryKey: getStatusGetQueryKey(),
      staleTime: 15_000,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    },
  });

  const warnedRef = useRef(false);
  useEffect(() => {
    if (query.isError && !warnedRef.current) {
      warnedRef.current = true;
      // eslint-disable-next-line no-console
      console.warn(
        '[useMaintenanceStatus] /api/status query failed; failing open (maintenance: false).',
        query.error,
      );
    } else if (!query.isError && warnedRef.current) {
      // Recovery — reset so a subsequent error streak warns again.
      warnedRef.current = false;
    }
  }, [query.isError, query.error]);

  return {
    // Mutator unwraps the envelope at runtime; the typed return is the
    // inner `StatusGet200Data` (`{ maintenance: boolean }`), not the wire
    // shape.
    maintenance: query.data?.maintenance ?? false,
    isError: query.isError,
    // React Query v5 — `isFetched` latches `true` on the FIRST settle (success
    // OR error) and never reverts, so `isLoading && !isFetched` is `true` ONLY
    // before that first settle (the genuine first in-flight load), never on a
    // later poll. `RootGate` gates its spinner on this, so the splash never
    // repaints once the query has settled — including the errored-first-load
    // case where `data` is never cached and a later failing poll CAN flip
    // `isLoading` back to `true` (the latched `isFetched` still pins it down).
    isInitialLoad: query.isLoading && !query.isFetched,
  };
}
